import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../../services/api';
import { useSSE } from '../../hooks/useSSE';
import { QRCodeSVG } from 'qrcode.react';

interface QueueStatus {
  storeName: string;
  storeLogo: string | null;
  isActive: boolean;
  currentServing: number;
  currentNumber: number;
  waitingCount: number;
  estimatedWaitMinutes: number;
  recentCalled: Array<{
    ticketNumber: number;
    status: string;
    calledAt: string | null;
  }>;
}

const DisplayBoardPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [status, setStatus] = useState<QueueStatus | null>(null);
  const [prevServing, setPrevServing] = useState<number>(0);
  const [isFlashing, setIsFlashing] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!storeId) return;
    try {
      const response = await publicApi.getStatus(storeId);
      const newStatus = response.data;

      // 叫號時閃爍動畫
      if (newStatus.currentServing !== prevServing && prevServing !== 0) {
        setIsFlashing(true);
        setTimeout(() => setIsFlashing(false), 3000);
      }
      setPrevServing(newStatus.currentServing);
      setStatus(newStatus);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  }, [storeId, prevServing]);

  // 初次載入
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // SSE 即時更新（事件觸發時重新 fetch）
  useSSE({
    storeId: storeId || '',
    enabled: !!storeId,
    onEvent: () => { fetchStatus(); },
  });

  // 降級備援：30 秒輪詢
  useEffect(() => {
    if (!storeId) return;
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [storeId, fetchStatus]);

  const queueUrl = `${window.location.origin}/q/${storeId}`;

  const getStatusLabel = (s: string) => {
    const labels: Record<string, string> = {
      CALLED: '叫號中',
      SERVING: '服務中',
      COMPLETED: '已完成',
    };
    return labels[s] || s;
  };

  if (!status) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-8">
      {/* 店名 */}
      <h1 className="text-2xl font-bold mb-8 text-gray-300">{status.storeName}</h1>

      {/* 目前叫號 - 超大字 */}
      <div className={`text-center mb-8 transition-all duration-500 ${isFlashing ? 'scale-110' : ''}`}>
        <p className="text-xl text-gray-400 mb-2">目前叫號</p>
        <p className={`text-9xl font-bold ${isFlashing ? 'text-yellow-400 animate-pulse' : 'text-white'}`}>
          #{status.currentServing || 0}
        </p>
      </div>

      {/* 等候資訊 */}
      <div className="flex gap-12 mb-12">
        <div className="text-center">
          <p className="text-4xl font-bold text-orange-400">{status.waitingCount}</p>
          <p className="text-gray-400">等候中</p>
        </div>
        <div className="text-center">
          <p className="text-4xl font-bold text-blue-400">~{status.estimatedWaitMinutes}</p>
          <p className="text-gray-400">預估等待（分鐘）</p>
        </div>
      </div>

      {/* 最近叫號紀錄 */}
      {status.recentCalled.length > 0 && (
        <div className="w-full max-w-md mb-8">
          <p className="text-gray-400 mb-3 text-center">最近叫號</p>
          <div className="space-y-2">
            {status.recentCalled.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between bg-slate-800 rounded-lg px-4 py-2">
                <span className="text-xl font-bold">#{item.ticketNumber}</span>
                <span className={`text-sm px-2 py-1 rounded ${
                  item.status === 'SERVING' ? 'bg-green-900 text-green-300' :
                  item.status === 'CALLED' ? 'bg-yellow-900 text-yellow-300' :
                  'bg-slate-700 text-gray-400'
                }`}>
                  {getStatusLabel(item.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* QR Code */}
      <div className="text-center">
        <p className="text-gray-400 mb-3">掃碼線上取號</p>
        <div className="bg-white p-3 rounded-lg inline-block">
          <QRCodeSVG value={queueUrl} size={120} />
        </div>
      </div>
    </div>
  );
};

export default DisplayBoardPage;
